/**
 * Delivery Detail View Component
 * Transport-style inline detail view with tabs
 * Shows full delivery details, lifecycle, payment, and history
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import type { Delivery } from '@/modules/commerce/delivery/types/delivery';
import { getStageFromStatus, STAGE_LABELS } from '@/modules/commerce/delivery/types/delivery';
import { StatusBadge, StageIndicator } from './StatusBadge';
import { updateDeliveryStatus, cancelDelivery } from '@/services/commerce/deliveryService';

interface DeliveryDetailViewProps {
  delivery: Delivery;
  loading?: boolean;
  onBack: () => void;
  onRefresh: () => void | Promise<void>;
  onEdit?: (delivery: Delivery) => void;
  onStatusUpdate?: (delivery: Delivery) => void;
}

type TabType = 'lifecycle' | 'actions' | 'details' | 'payment' | 'history';

// Format date helper
const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Format amount helper
const formatAmount = (amount?: number | null): string => {
  if (!amount) return 'N/A';
  return `₹${amount.toLocaleString('en-IN')}`;
};

export function DeliveryDetailView({
  delivery,
  loading = false,
  onBack,
  onRefresh,
  onEdit,
  onStatusUpdate,
}: DeliveryDetailViewProps) {
  const [activeTab, setActiveTab] = useState<TabType>('lifecycle');
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const stage = delivery.stage ?? getStageFromStatus(delivery.status);
  const stageLabel = STAGE_LABELS[stage] || 'Unknown';
  const isActive = !['COMPLETED', 'CANCELLED', 'FAILED'].includes(delivery.status?.toUpperCase() || '');
  const canEdit = isActive && delivery.status?.toUpperCase() !== 'DELIVERED';
  const isRefreshing = loading || refreshing;

  // Handle refresh with visual feedback
  const handleRefreshClick = useCallback(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  }, [onRefresh]);

  // Tab definitions
  const tabs: { id: TabType; label: string; show: boolean }[] = [
    { id: 'lifecycle', label: 'Lifecycle', show: true },
    { id: 'actions', label: 'Actions', show: isActive },
    { id: 'details', label: 'Details', show: true },
    { id: 'payment', label: 'Payment', show: true },
    { id: 'history', label: 'History', show: true },
  ];

  // Handle status update
  const handleStatusUpdate = async (newStatus: string) => {
    try {
      setActionLoading(true);
      await updateDeliveryStatus(delivery.id, newStatus);
      onRefresh();
      onStatusUpdate?.(delivery);
    } catch (err: any) {
      console.error('Failed to update status:', err);
      alert(err.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle cancel delivery
  const handleCancelDelivery = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }
    try {
      setActionLoading(true);
      await cancelDelivery(delivery.id, cancelReason);
      setShowCancelDialog(false);
      setCancelReason('');
      onRefresh();
      onStatusUpdate?.(delivery);
    } catch (err: any) {
      console.error('Failed to cancel delivery:', err);
      alert(err.message || 'Failed to cancel delivery');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to List
        </button>
        <div className="flex gap-2">
          {canEdit && onEdit && (
            <button
              onClick={() => onEdit(delivery)}
              className="flex items-center gap-2 px-4 py-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
          )}
          <button
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition disabled:opacity-50"
          >
            <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Delivery Summary Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Delivery #{delivery.requestNumber || delivery.deliveryNumber || `REQ-${delivery.id}`}
            </h2>
            <p className="text-gray-600 mt-1">
              Created: {formatDate(delivery.createdAt)}
            </p>
            {delivery.salesOrderId && (
              <p className="text-sm text-gray-500 mt-1">
                Sales Order: #{delivery.salesOrderId}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {delivery.isUrgent && (
              <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                Urgent
              </span>
            )}
            <StatusBadge status={delivery.status} />
          </div>
        </div>

        {/* Stage Progress Indicator */}
        <StageProgressBar stage={stage} stageLabel={stageLabel} status={delivery.status} />

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Stage Progress</p>
            <div className="flex items-center gap-2">
              <StageIndicator stage={stage} size="md" />
            </div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Amount</p>
            <p className="text-xl font-bold text-gray-900">
              {formatAmount(delivery.finalAmount || delivery.estimatedAmount)}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Priority</p>
            <p className={`text-xl font-bold ${delivery.isUrgent ? 'text-red-600' : 'text-orange-600'}`}>
              {delivery.priority || 'Normal'}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500 mb-1">Distance</p>
            <p className="text-xl font-bold text-gray-900">
              {delivery.estimatedDistanceKm ? `${delivery.estimatedDistanceKm} km` : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 bg-white rounded-t-lg">
        <nav className="flex gap-4 px-4 overflow-x-auto">
          {tabs
            .filter((tab) => tab.show)
            .map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-b-lg shadow p-6">
        {activeTab === 'lifecycle' && <LifecycleTab delivery={delivery} stage={stage} />}
        {activeTab === 'actions' && (
          <ActionsTab
            delivery={delivery}
            stage={stage}
            loading={actionLoading}
            onStatusUpdate={handleStatusUpdate}
            onShowCancelDialog={() => setShowCancelDialog(true)}
          />
        )}
        {activeTab === 'details' && <DetailsTab delivery={delivery} />}
        {activeTab === 'payment' && <PaymentTab delivery={delivery} />}
        {activeTab === 'history' && <HistoryTab delivery={delivery} />}
      </div>

      {/* Cancel Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Delivery</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel this delivery? This action cannot be undone.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Please provide a reason for cancellation..."
              className="w-full border border-gray-300 rounded-lg p-3 mb-4 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleCancelDelivery}
                disabled={actionLoading || !cancelReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {actionLoading ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stage Progress Bar Component
function StageProgressBar({ stage, stageLabel, status }: { stage: number; stageLabel: string; status: string }) {
  const stages = [
    { num: 1, label: 'Created' },
    { num: 2, label: 'Assigned' },
    { num: 3, label: 'In Transit' },
    { num: 4, label: 'Delivered' },
    { num: 5, label: 'Completed' },
  ];

  const isCancelled = stage === 0;

  return (
    <div className="mt-4">
      {isCancelled ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <span className="text-red-600 font-medium">Delivery {status?.replace(/_/g, ' ')}</span>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          {stages.map((s, idx) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                    s.num < stage
                      ? 'bg-green-500 text-white'
                      : s.num === stage
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s.num < stage ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    s.num
                  )}
                </div>
                <span className={`text-xs mt-1 ${s.num <= stage ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                  {s.label}
                </span>
              </div>
              {idx < stages.length - 1 && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    s.num < stage ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Lifecycle Tab
function LifecycleTab({ delivery, stage }: { delivery: Delivery; stage: number }) {
  const driverDesc = delivery.driverName
    ? `Driver: ${delivery.driverName}${delivery.vehicleNumber ? ` · ${delivery.vehicleNumber}` : ''}`
    : 'Driver and vehicle assigned';

  const lifecycleSteps = [
    { status: 'REQUEST_CREATED', label: 'Request Created', description: 'Delivery request initiated', stage: 1 },
    { status: 'ASSIGNED',        label: 'Driver Assigned', description: driverDesc, stage: 2 },
    { status: 'IN_TRANSIT',      label: 'In Transit',      description: 'Order picked up and on the way', stage: 3 },
    { status: 'DELIVERED',       label: 'Delivered — Awaiting Customer Approval', description: 'Approval notification sent via email / WhatsApp', stage: 4 },
    { status: 'COMPLETED',       label: 'Completed',       description: 'Customer approved, order closed', stage: 5 },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Delivery Lifecycle</h3>
      <div className="relative">
        {lifecycleSteps.map((step, idx) => {
          const isCompleted = step.stage < stage;
          const isCurrent = step.stage === stage;
          const isPending = step.stage > stage;

          return (
            <div key={step.status} className="flex gap-4 pb-6 last:pb-0">
              {/* Timeline line */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isCompleted
                      ? 'bg-green-500 text-white'
                      : isCurrent
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    step.stage
                  )}
                </div>
                {idx < lifecycleSteps.length - 1 && (
                  <div
                    className={`w-0.5 flex-1 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`}
                  />
                )}
              </div>
              {/* Content */}
              <div className="flex-1 pb-4">
                <p className={`font-medium ${isPending ? 'text-gray-400' : 'text-gray-900'}`}>
                  {step.label}
                </p>
                <p className={`text-sm ${isPending ? 'text-gray-300' : 'text-gray-500'}`}>
                  {step.description}
                </p>
                {isCurrent && (
                  <span className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                    Current Stage
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Details Tab
function DetailsTab({ delivery }: { delivery: Delivery }) {
  return (
    <div className="space-y-6">
      {/* Pickup Details */}
      <div className="border rounded-lg p-4">
        <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          Pickup Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500 mb-1">Address</p>
            <p className="font-medium">{delivery.pickupAddress || 'N/A'}</p>
            {(delivery.pickupCity || delivery.pickupState || delivery.pickupPincode) && (
              <p className="text-sm text-gray-600">
                {[delivery.pickupCity, delivery.pickupState, delivery.pickupPincode].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Contact Person</p>
            <p className="font-medium">{delivery.pickupContactName || 'N/A'}</p>
            {delivery.pickupContactPhone && (
              <p className="text-sm text-gray-600">{delivery.pickupContactPhone}</p>
            )}
          </div>
        </div>
      </div>

      {/* Drop/Delivery Details */}
      <div className="border rounded-lg p-4">
        <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
          </svg>
          Delivery Details
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {delivery.customerName && (
            <div className="col-span-2">
              <p className="text-sm text-gray-500 mb-1">Customer Name</p>
              <p className="font-medium text-blue-900">{delivery.customerName}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-gray-500 mb-1">Address</p>
            <p className="font-medium">{delivery.dropAddress || delivery.deliveryAddress || 'N/A'}</p>
            {(delivery.dropCity || delivery.dropState || delivery.dropPincode) && (
              <p className="text-sm text-gray-600">
                {[delivery.dropCity, delivery.dropState, delivery.dropPincode].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Contact Person</p>
            <p className="font-medium">{delivery.dropContactName || delivery.contactPerson || 'N/A'}</p>
            {(delivery.dropContactPhone || delivery.contactPhone) && (
              <p className="text-sm text-gray-600">{delivery.dropContactPhone || delivery.contactPhone}</p>
            )}
          </div>
        </div>
      </div>

      {/* Driver & Vehicle Details */}
      <div className="border rounded-lg p-4">
        <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
          Driver & Vehicle
        </h4>
        {delivery.driverName ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Driver</p>
              <p className="font-medium">{delivery.driverName}</p>
              {delivery.driverPhone && (
                <p className="text-sm text-gray-600">{delivery.driverPhone}</p>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Vehicle</p>
              <p className="font-medium">{delivery.vehicleNumber || 'N/A'}</p>
              {delivery.vehicleType && (
                <p className="text-sm text-gray-600">{delivery.vehicleType}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              No driver assigned yet. Waiting for assignment.
            </p>
          </div>
        )}
      </div>

      {/* Product Details */}
      {(delivery.productType || delivery.quantity) && (
        <div className="border rounded-lg p-4">
          <h4 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Product Details
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Product Type</p>
              <p className="font-medium">{delivery.productType || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Quantity</p>
              <p className="font-medium">
                {delivery.quantity || 'N/A'} {delivery.quantityUnit || ''}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Special Instructions</p>
              <p className="font-medium">{delivery.specialInstructions || 'None'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Notes */}
      {delivery.notes && (
        <div className="border rounded-lg p-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Notes</h4>
          <p className="text-gray-600">{delivery.notes}</p>
        </div>
      )}
    </div>
  );
}

// Payment Tab
function PaymentTab({ delivery }: { delivery: Delivery }) {
  const estimatedAmount = delivery.estimatedAmount || 0;
  const finalAmount = delivery.finalAmount || 0;
  const isPaid = ['PAYMENT_RECEIVED', 'COMPLETED'].includes(delivery.status?.toUpperCase() || '');
  const isPending = delivery.status?.toUpperCase() === 'PAYMENT_PENDING';

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Payment Information</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="p-6 bg-gray-50 rounded-lg border">
          <p className="text-sm text-gray-500 mb-2">Estimated Amount</p>
          <p className="text-3xl font-bold text-gray-900">
            {formatAmount(estimatedAmount)}
          </p>
        </div>

        <div className={`p-6 rounded-lg border ${isPaid ? 'bg-green-50 border-green-200' : isPending ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50'}`}>
          <p className="text-sm text-gray-500 mb-2">Final Amount</p>
          <p className={`text-3xl font-bold ${isPaid ? 'text-green-600' : 'text-gray-900'}`}>
            {formatAmount(finalAmount) || 'Pending'}
          </p>
          {isPaid && (
            <span className="inline-flex items-center gap-1 mt-2 text-sm text-green-700">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              Payment Received
            </span>
          )}
          {isPending && (
            <span className="inline-flex items-center gap-1 mt-2 text-sm text-yellow-700">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
              </svg>
              Awaiting Payment
            </span>
          )}
        </div>
      </div>

      {/* Payment Status */}
      <div className="border rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3">Payment Status</h4>
        <div className="flex items-center gap-4">
          <div className={`px-4 py-2 rounded-lg font-medium ${
            isPaid ? 'bg-green-100 text-green-800' :
            isPending ? 'bg-yellow-100 text-yellow-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {isPaid ? 'Paid' : isPending ? 'Pending' : 'Not Yet'}
          </div>
          {delivery.completedAt && (
            <span className="text-sm text-gray-500">
              Completed: {formatDate(delivery.completedAt)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Actions Tab - Status Progression (Transport-style)
interface ActionsTabProps {
  delivery: Delivery;
  stage: number;
  loading: boolean;
  onStatusUpdate: (status: string) => void;
  onShowCancelDialog: () => void;
}

// Status progression map — each status has exactly ONE valid next step.
// Do NOT add skip-ahead shortcuts here; they bypass driver acceptance and
// customer approval, causing the "DELIVERED before driver accepts" bug.
const STATUS_ACTIONS: Record<string, { nextStatus: string; label: string; color: string }> = {
  'REQUEST_CREATED': { nextStatus: 'BROADCASTING', label: 'Broadcast to Drivers', color: 'bg-indigo-600 hover:bg-indigo-700' },
  'PENDING':         { nextStatus: 'BROADCASTING', label: 'Broadcast to Drivers', color: 'bg-indigo-600 hover:bg-indigo-700' },
  // BROADCASTING: admin waits for driver to accept — no manual jump to ASSIGNED
  'ASSIGNED':        { nextStatus: 'ACCEPTED',     label: 'Confirm Driver Accepted', color: 'bg-blue-600 hover:bg-blue-700' },
  'ACCEPTED':        { nextStatus: 'IN_TRANSIT',   label: 'Start Trip',              color: 'bg-yellow-600 hover:bg-yellow-700' },
  'IN_TRANSIT':      { nextStatus: 'ARRIVED_PICKUP', label: 'Arrived at Pickup',     color: 'bg-yellow-600 hover:bg-yellow-700' },
  'ARRIVED_PICKUP':  { nextStatus: 'PICKED_UP',    label: 'Confirm Pickup',          color: 'bg-orange-600 hover:bg-orange-700' },
  'PICKED_UP':       { nextStatus: 'ARRIVED_DROP', label: 'Arrived at Drop',         color: 'bg-orange-600 hover:bg-orange-700' },
  'ARRIVED_DROP':    { nextStatus: 'DELIVERED',    label: 'End Trip / Mark Delivered', color: 'bg-green-600 hover:bg-green-700' },
  // DELIVERED = awaiting customer approval via email/WhatsApp — complete only after approval
  'DELIVERED':       { nextStatus: 'COMPLETED',    label: 'Customer Approved — Complete', color: 'bg-emerald-600 hover:bg-emerald-700' },
  'PAYMENT_PENDING': { nextStatus: 'COMPLETED',    label: 'Complete Delivery',        color: 'bg-green-600 hover:bg-green-700' },
};

function ActionsTab({ delivery, stage, loading, onStatusUpdate, onShowCancelDialog }: ActionsTabProps) {
  const currentStatus = delivery.status?.toUpperCase() || '';
  const action = STATUS_ACTIONS[currentStatus];
  const isFinalState = ['COMPLETED', 'CANCELLED', 'FAILED'].includes(currentStatus);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Available Actions</h3>

      {/* Current Status */}
      <div className="p-4 bg-gray-50 rounded-lg border">
        <p className="text-sm text-gray-500 mb-2">Current Status</p>
        <div className="flex items-center gap-3">
          <StatusBadge status={delivery.status} />
          <span className="text-gray-600">Stage {stage} of 5</span>
        </div>
      </div>

      {/* Broadcasting — waiting for driver to accept, no manual jump allowed */}
      {currentStatus === 'BROADCASTING' && (
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start gap-3">
          <svg className="w-5 h-5 text-indigo-600 mt-0.5 animate-pulse flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/>
          </svg>
          <div>
            <p className="text-sm font-medium text-indigo-800">Broadcasting to nearby drivers…</p>
            <p className="text-xs text-indigo-700 mt-1">
              Waiting for a driver to accept the request. Status will automatically update to
              "Driver Assigned" once a driver accepts. Go to Transport module to manually assign a driver.
            </p>
          </div>
        </div>
      )}

      {/* Status Progression Actions (only for statuses that have a defined next step) */}
      {!isFinalState && currentStatus !== 'BROADCASTING' && action && (
        <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-medium text-gray-900 mb-4">Next Step in Delivery Lifecycle</h4>
          <button
            onClick={() => onStatusUpdate(action.nextStatus)}
            disabled={loading}
            className={`px-6 py-3 text-white rounded-lg font-medium transition disabled:opacity-50 flex items-center gap-2 ${action.color}`}
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                {action.label}
              </>
            )}
          </button>
          <p className="text-sm text-gray-600 mt-3">
            Updates status from <strong>{currentStatus.replace(/_/g, ' ')}</strong> → <strong>{action.nextStatus.replace(/_/g, ' ')}</strong>
          </p>
        </div>
      )}

      {/* Awaiting Customer Approval notice */}
      {currentStatus === 'DELIVERED' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm font-medium text-amber-800">⏳ Awaiting Customer Approval</p>
          <p className="text-xs text-amber-700 mt-1">
            Delivery completed. An approval notification has been sent to the customer via email / WhatsApp.
            Click "Customer Approved — Complete" above once the customer confirms receipt.
          </p>
        </div>
      )}

      {/* Cancel Delivery */}
      {!isFinalState && (
        <div className="p-6 bg-red-50 rounded-lg border border-red-200">
          <h4 className="font-medium text-red-900 mb-2">Cancel Delivery</h4>
          <p className="text-sm text-red-700 mb-4">
            Cancel this delivery if it cannot be fulfilled. This action requires a reason and cannot be undone.
          </p>
          <button
            onClick={onShowCancelDialog}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel Delivery
          </button>
        </div>
      )}

      {/* Final State Message */}
      {isFinalState && (
        <div className={`p-6 rounded-lg border ${
          currentStatus === 'COMPLETED' ? 'bg-green-50 border-green-200' :
          currentStatus === 'CANCELLED' ? 'bg-red-50 border-red-200' :
          'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            {currentStatus === 'COMPLETED' && (
              <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {currentStatus === 'CANCELLED' && (
              <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <div>
              <p className={`font-medium ${
                currentStatus === 'COMPLETED' ? 'text-green-800' :
                currentStatus === 'CANCELLED' ? 'text-red-800' :
                'text-gray-800'
              }`}>
                Delivery {currentStatus.replace(/_/g, ' ')}
              </p>
              <p className="text-sm text-gray-600">
                No further actions available for this delivery.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// History Tab
function HistoryTab({ delivery }: { delivery: Delivery }) {
  // Since we don't have status history from the API yet, show what we have
  const events = [
    {
      date: delivery.createdAt,
      label: 'Delivery Created',
      description: `Delivery request created with status ${delivery.status}`,
    },
    delivery.updatedAt && delivery.updatedAt !== delivery.createdAt && {
      date: delivery.updatedAt,
      label: 'Last Updated',
      description: 'Delivery information was updated',
    },
    delivery.completedAt && {
      date: delivery.completedAt,
      label: 'Completed',
      description: `Delivery completed${delivery.completedBy ? ` by ${delivery.completedBy}` : ''}`,
    },
  ].filter(Boolean) as { date?: string; label: string; description: string }[];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Delivery History</h3>

      {events.length > 0 ? (
        <div className="space-y-4">
          {events.map((event, idx) => (
            <div key={idx} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{event.label}</p>
                <p className="text-sm text-gray-500">{event.description}</p>
                <p className="text-xs text-gray-400 mt-1">{formatDate(event.date)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg">
          No history available
        </div>
      )}
    </div>
  );
}

export default DeliveryDetailView;
